import React, { useState, useEffect, ChangeEvent, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { AuthenticationForm } from "@components/ui";
import { CheckBox as FormCheckBox } from "@components/ui";

import {
  clearLocalStorage,
  markStateAsUsed,
  ComposeURLWithGetQueryArguments,
} from "@shared/utils";
import { PagePaths } from "@shared/constants";
import { ApiError } from "@shared/services";
import { delay } from "@shared/utils";

import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@shared/hooks/useDocumentTitle";
import { t_createAccount } from "@i18n/usualTexts/t_createAccount";

import { ActionButton } from "@components/ui";
import { useIsMobileButtons } from "@shared/hooks/useIsMobileButtons";

import { useOAuth2 } from "@shared/contexts";
import { useRecaptchaContext } from "@shared/contexts/RecaptchaContext";
import { jwtDecode } from "jwt-decode";
import i18n from "@i18n/index";
import { t_onboarding } from "@i18n/usualTexts/t_onboarding";
import { CommitOnBoardingPost } from "@features/createaccount/service/AccountRequest";

import { useAuthError } from "@shared/hooks/useAuthError";
import { getHousienUrl } from "@shared/utils/housienUrl";
import { GoogleAuthenticationCommitRequest } from "@shared/services";

export interface FormData {
  acceptTerms: {
    value: string;
    errors: Set<string>;
  };
}

const OnBoardingPage: React.FC = () => {
  const { t } = useTranslation();
  useDocumentTitle(t("signup.onboarding.pageTitle"));
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [serverErrorPopUp, setServerErrorPopUp] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isMobileButtons = useIsMobileButtons();

  const { state } = useOAuth2();
  const { currentToken: antiBotToken, executeRecaptcha, isReady: isRecaptchaReadyCheck } = useRecaptchaContext();
  const { handleRegisterError } = useAuthError();
  const [isRecaptchaReady, setIsRecaptchaReady] = useState<boolean>(false);
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
  const user_name = JSON.parse(JSON.stringify(jwtDecode(state))).additional_claims.given_name;

  const [formData, setFormData] = useState<FormData>({
    acceptTerms: { value: "", errors: new Set<string>() },
  });

  useEffect(() => {
    let isMounted = true;
    let animationFrameId: number | null = null;
    const maxWaitTime = 10000;
    const startTime = Date.now();

    const isReadyNow = (): boolean => !!(isRecaptchaReadyCheck && isRecaptchaReadyCheck());
    const isTimedOut = (): boolean => Date.now() - startTime > maxWaitTime;

    const waitForRecaptchaReady = (): Promise<boolean> => {
      return new Promise((resolve) => {
        const checkReady = () => {
          if (!isMounted) { resolve(false); return; }
          if (isReadyNow()) { resolve(true); return; }
          if (isTimedOut()) { resolve(false); return; }
          animationFrameId = requestAnimationFrame(checkReady);
        };
        checkReady();
      });
    };

    const setReady = () => { if (isMounted) { setIsRecaptchaReady(true); setRecaptchaError(null); } };

    const executeRecaptchaToken = async () => {
      if (!executeRecaptcha) {
        throw new Error(i18n.t('error.runtime.recaptchaUnavailable'));
      }
      const isReady = await waitForRecaptchaReady();
      if (!isReady) {
        throw new Error(i18n.t('error.runtime.recaptchaNotReadyTimeout'));
      }
      const recaptchaResult = await executeRecaptcha();
      if (!recaptchaResult.success) {
        throw new Error(`${i18n.t('error.runtime.recaptchaFailed')}: ${recaptchaResult.error || 'Unknown error'}`);
      }
    };

    const ensureRecaptchaReady = async () => {
      try {
        if (antiBotToken) { setReady(); return; }
        await executeRecaptchaToken();
        setReady();
      } catch (error) {
        if (!isMounted) return;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setRecaptchaError(errorMessage);
        setIsRecaptchaReady(false);
      }
    };

    ensureRecaptchaReady();

    return () => {
      isMounted = false;
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
    };
  }, [antiBotToken, executeRecaptcha, isRecaptchaReadyCheck]);

  function handleCheckBox(e: ChangeEvent<HTMLInputElement>) {
    const { checked } = e.target;
    setFormData((prev) => ({
      acceptTerms: {
        value: checked ? "true" : "",
        errors: new Set<string>(),
      },
    }));
  }

  function validateFormData(currentFormData: FormData): boolean {
    const termsAccepted = currentFormData.acceptTerms.value === "true";
    if (!termsAccepted) {
      setFormData({
        acceptTerms: {
          ...currentFormData.acceptTerms,
          errors: new Set([t("warningTexts.signup.acceptTerms")]),
        },
      });
      return false;
    }
    return true;
  }

  function buildOnBoardingPayload(currentFormData: FormData): GoogleAuthenticationCommitRequest {
    return {
      terms: currentFormData.acceptTerms.value === "true",
    };
  }

  function isFacebookProvider(decoded: any): boolean {
    return decoded.additional_claims?.provider === "facebook" ||
      (decoded.iss?.includes("facebook") ?? false);
  }

  function determineProvider(stateToken: string): "google" | "facebook" {
    if (!stateToken) return "google";
    try {
      const decoded = jwtDecode(stateToken) as any;
      return isFacebookProvider(decoded) ? "facebook" : "google";
    } catch (e) {
      console.warn("Failed to decode token to determine provider, defaulting to google", e);
      return "google";
    }
  }

  function storeSocialLoginToken(commitOnBoardingPostResponse: any): void {
    if (!commitOnBoardingPostResponse.parameters) return;
    try {
      sessionStorage.setItem("socialLoginToken", JSON.stringify({
        access_token: state,
        token_type: "Bearer",
        expiration_time: Math.floor(Date.now() / 1000) + 3600,
        issued_at: Math.floor(Date.now() / 1000),
        next_step: PagePaths.LOGIN.VERIFYEMAIL,
      }));
    } catch (e) {
      console.warn("Failed to store social login token:", e);
    }
  }

  function handleSocialLoginRedirect(commitOnBoardingPostResponse: any): boolean {
    try {
      const isSocialLogin = sessionStorage.getItem("isSocialLogin") === "true";
      if (!isSocialLogin) return false;
      storeSocialLoginToken(commitOnBoardingPostResponse);
      window.location.href = ComposeURLWithGetQueryArguments(PagePaths.LOGIN.VERIFYEMAIL);
      return true;
    } catch (e) {
      console.warn("Failed to check social login status:", e);
      return false;
    }
  }

  function handleAuthenticationError(error: unknown): boolean {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.IDENTITY, { sessionExpired: "true" }));
      setIsLoading(false);
      return true;
    }
    return false;
  }

  async function postFormDatas_and_navigate(
    onBoardingData: GoogleAuthenticationCommitRequest
  ) {
    const provider = determineProvider(state);

    let tokenToSend = antiBotToken;
    if (!tokenToSend && executeRecaptcha) {
      const result = await executeRecaptcha();
      if (result.success && result.token) {
        tokenToSend = result.token;
      }
    }

    const commitOnBoardingPostResponse = await CommitOnBoardingPost(
      onBoardingData,
      state,
      tokenToSend || undefined,
      provider
    );

    clearLocalStorage();

    if (state) {
      markStateAsUsed(state);
    }

    if (handleSocialLoginRedirect(commitOnBoardingPostResponse)) {
      return;
    }

    window.location.href = commitOnBoardingPostResponse.parameters?.redirect_uri;
  }

  function frontExceptionFunction(errorDescription: Array<string>) {
    const newFormData = { ...formData };
    const field = newFormData[errorDescription[0] as keyof FormData] as any;
    if (field) {
      field.errors.add(errorDescription[1]);
      setFormData(newFormData);
    }
  }

  async function handleSubmit() {
    setIsLoading(true);
    setSubmitted(true);

    try {
      const isValid = validateFormData(formData);
      if (!isValid) { setIsLoading(false); return; }

      const onBoardingPayload = buildOnBoardingPayload(formData);
      await postFormDatas_and_navigate(onBoardingPayload);
    } catch (e) {
      if (handleAuthenticationError(e)) return;
      const error = JSON.parse(JSON.stringify(e));
      handleRegisterError(error, setServerErrorPopUp, frontExceptionFunction);
      setIsLoading(false);
    } finally {
      setFormData((prev) => ({ ...prev }));
      await delay(1000);
      setSubmitted(false);
    }
  }

  const handleMobileSubmit = useCallback(() => {
    if (isLoading) return;
    const formElement = document.querySelector("form");
    if (formElement) {
      const submitEvent = new Event("submit", { bubbles: true, cancelable: true });
      formElement.dispatchEvent(submitEvent);
    }
  }, [isLoading]);

  const onboardingMobileFooter = isMobileButtons ? (
    <div className="text-end">
      <ActionButton submit isLoading={isLoading} className="h-12" onClick={handleMobileSubmit} />
    </div>
  ) : undefined;

  return (
    <AuthenticationForm
      title={t(t_onboarding.title, { name: user_name })}
      subtitle={t(t_onboarding.subtitle)}
      onSubmit={() => handleSubmit()}
      isLoading={isLoading}
      serverErrorPopUp={serverErrorPopUp}
      setServerErrorPopUp={setServerErrorPopUp}
      mobileFooter={onboardingMobileFooter}
    >
      <p className="text-sm text-muted-foreground">
        {t(t_onboarding.body)}
      </p>

      <div className="mt-6">
        <FormCheckBox
          name="acceptTerms"
          required={true}
          onChange={handleCheckBox}
          value={formData.acceptTerms.value}
          errors={formData.acceptTerms.errors}
          submitted={submitted}
          termsLink={t(t_createAccount.serviceTermsLink)}
          onTermsClick={() => window.open(`${getHousienUrl()}/serviceterms/latest`, "_blank")}
          privacyPolicyLink={t(t_createAccount.privacyPolicyLink)}
          onPrivacyPolicyClick={() => window.open(`${getHousienUrl()}/privacy/latest`, "_blank")}
        >
          <span>{t(t_createAccount.serviceTermsCheck)}</span>
        </FormCheckBox>
      </div>

      {!isMobileButtons && (
        <div className="mt-8 text-end">
          <ActionButton submit isLoading={isLoading} />
        </div>
      )}
    </AuthenticationForm>
  );
};

export default OnBoardingPage;
